import type { SingularKeys } from '@libs/i18n';
import type { SxProps, Theme } from '@mui/material';

/**
 * Admin-panel accent palette. Mirrors the design's `C` tokens, mapped onto the
 * app theme where a token exists and falling back to the design's literal hex
 * for the few accents (teal, the violet "elevated" tint) that aren't in the
 * shared palette.
 */
export const adminColors = (theme: Theme) => ({
  ink: theme.palette.text.primary,
  sub: theme.palette.text.secondary,
  faint: theme.palette.text.tertiary,
  border: theme.palette.grey[300],
  surface: theme.palette.background.paper,
  canvas: theme.palette.background.default,
  primary: theme.palette.primary.main,
  azure: theme.palette.accent.blue,
  green: theme.palette.accent.green,
  warn: theme.palette.warning.main,
  error: theme.palette.error.main,
  orange: theme.palette.orange[500],
  violet: theme.palette.ocean[700],
  violetBg: theme.palette.ocean[100],
  violetBorder: theme.palette.ocean[300],
  teal: '#14B8A6',
});


/**
 * Theme-aware button styles for the admin panel.
 *
 * The global `MuiButton` override fills every button with `secondary.main` and
 * white text unless overridden, so ad-hoc admin buttons that only set a text
 * color rendered as solid black pills, and buttons that hardcoded `color:'#fff'`
 * turned invisible in dark mode (white fill + white text). These helpers derive
 * every color from the palette so buttons read correctly in both themes:
 *
 * - `solid`   — ink fill (black in light / white in dark) with contrasting text.
 * - `neutral` — muted grey fill (cancel / secondary actions).
 * - `ghost`   — transparent fill for text/link-style buttons.
 */
export const adminButtonSx = (theme: Theme): Record<'solid' | 'neutral' | 'ghost', SxProps<Theme>> => {
  const c = adminColors(theme);
  return {
    solid: {
      textTransform: 'none',
      fontWeight: 500,
      backgroundColor: c.ink,
      color: theme.palette.background.paper,
      boxShadow: 'none',
      '&:hover': { backgroundColor: c.ink, opacity: 0.9, boxShadow: 'none' },
      '&.Mui-disabled': {
        backgroundColor: c.ink,
        color: theme.palette.background.paper,
        opacity: 0.45,
      },
    },
    neutral: {
      textTransform: 'none',
      backgroundColor: theme.palette.grey[200],
      color: c.ink,
      boxShadow: 'none',
      '&:hover': { backgroundColor: theme.palette.grey[300], boxShadow: 'none' },
    },
    ghost: {
      textTransform: 'none',
      backgroundColor: 'transparent',
      color: c.sub,
      boxShadow: 'none',
      '&:hover': { backgroundColor: theme.palette.action.hover, boxShadow: 'none' },
    },
  };
};

export type AdminStatusTone = {
  /** Soft background tint. */
  bg: string;
  /** Border color. */
  border: string;
  /** Foreground text / dot color. */
  fg: string;
  /** i18n label key. */
  labelKey: SingularKeys;
  /** Whether the chip should render a spinner (in-progress states). */
  spin?: boolean;
};

/**
 * status → tone map, mirroring the design's `STATUS` table. Covers tenant,
 * assessment and document classification statuses. Unknown values fall back to
 * the neutral `draft` tone.
 */
export const ADMIN_STATUS: Record<string, AdminStatusTone> = {
  active: { bg: '#F0FBF5', border: '#B7EBCE', fg: '#157A47', labelKey: 'app.admin.status.active' },
  processing: {
    bg: '#EFF6FF',
    border: '#BEDBFF',
    fg: '#1F6FE0',
    labelKey: 'app.admin.status.processing',
    spin: true,
  },
  provisioning: {
    bg: '#FFFBE6',
    border: '#FFE58F',
    fg: '#B57A00',
    labelKey: 'app.admin.status.provisioning',
    spin: true,
  },
  suspended: {
    bg: '#FAFAFA',
    border: '#E0E0E0',
    fg: '#707070',
    labelKey: 'app.admin.status.suspended',
  },
  failed: { bg: '#FFF1F0', border: '#FFCCC7', fg: '#C0272E', labelKey: 'app.admin.status.failed' },
  draft: { bg: '#F5F5F5', border: '#E0E0E0', fg: '#707070', labelKey: 'app.admin.status.draft' },
  submitted: {
    bg: '#FFF7E6',
    border: '#FFD591',
    fg: '#B57A00',
    labelKey: 'app.admin.status.submitted',
    spin: true,
  },
  completed: {
    bg: '#F0FBF5',
    border: '#B7EBCE',
    fg: '#157A47',
    labelKey: 'app.admin.status.completed',
  },
  valid: { bg: '#F0FBF5', border: '#B7EBCE', fg: '#157A47', labelKey: 'app.admin.status.valid' },
  invalid: {
    bg: '#FFF7E6',
    border: '#FFD591',
    fg: '#B57A00',
    labelKey: 'app.admin.status.invalid',
  },
};

/** Confidence category → i18n label + color role. */
export const CONFIDENCE_META: Record<
  'high' | 'medium' | 'low',
  { labelKey: SingularKeys; tone: 'green' | 'warn' | 'error' }
> = {
  high: { labelKey: 'app.admin.confidence.high', tone: 'green' },
  medium: { labelKey: 'app.admin.confidence.medium', tone: 'warn' },
  low: { labelKey: 'app.admin.confidence.low', tone: 'error' },
};
